import { useMemo } from "react";
import {
  AlertTriangle,
  Check,
  GraduationCap,
  Target,
  Award,
  Users,
  Monitor,
} from "lucide-react";
import {
  buildTrainingReadinessPct,
  buildTrainingGaugeStyle,
  buildTrainingHeroCopy,
  buildTrainingHeroKpis,
  buildCtetDonutView,
  buildNishthaDonutView,
  buildTrainingDemandBars,
  buildProgramCards,
  buildProgramWatchlist,
  buildTrainingActions,
} from "@/utils/ctteacherTrainingDerived";
import { toTitleCase } from "../utils/toTitleCase";

const KPI_ICONS = {
  green: Check,
  orange: AlertTriangle,
  "": GraduationCap,
  purple: Target,
};

const PROGRAM_ICONS = {
  red: Award,
  "": Users,
  green: GraduationCap,
  orange: Monitor,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  purple: Award,
  green: GraduationCap,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fmtPct = (n) => `${Number(n ?? 0).toFixed(1)}%`;

const CTTeacherTrainingTab = ({
  certification = null,
  trainingData = [],
}) => {
  const readinessPct = useMemo(
    () => buildTrainingReadinessPct(certification),
    [certification]
  );
  const gaugeStyle = useMemo(
    () => buildTrainingGaugeStyle(readinessPct),
    [readinessPct]
  );
  const heroCopy = useMemo(
    () => buildTrainingHeroCopy(certification, trainingData),
    [certification, trainingData]
  );
  const heroKpis = useMemo(
    () => buildTrainingHeroKpis(certification, trainingData),
    [certification, trainingData]
  );
  const ctetDonut = useMemo(() => buildCtetDonutView(certification), [certification]);
  const nishthaDonut = useMemo(() => buildNishthaDonutView(certification), [certification]);
  const demandBars = useMemo(
    () => buildTrainingDemandBars(trainingData, certification),
    [trainingData, certification]
  );
  const programCards = useMemo(
    () => buildProgramCards(trainingData, certification),
    [trainingData, certification]
  );
  const watchlist = useMemo(
    () => buildProgramWatchlist(trainingData, certification),
    [trainingData, certification]
  );
  const actions = useMemo(() => buildTrainingActions(certification), [certification]);

  return (
    <div className="cttr-training-tab" data-testid="ctteacher-training">
      <section className="cttr-hero-card" id="cttr-hero">
        <div className="cttr-hero-layout">
          <div>
            <div className="cttr-health-main">
              <div
                className="cttr-gauge"
                style={gaugeStyle}
                aria-label={`NISHTHA completion ${readinessPct} percent`}
              >
                <div>
                  <strong>{fmtPct(readinessPct)}</strong>
                  <span>Ready</span>
                </div>
              </div>
              <div className="cttr-health-copy">
                <h2>{toTitleCase(heroCopy.headline)}</h2>
                <p>{heroCopy.narrative}</p>
                <div className="cttr-status-row">
                  {heroCopy.chips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`cttr-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="cttr-summary-note">
              <div className="cttr-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary action for district review</b>
                <span>{heroCopy.primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="cttr-kpi-grid" aria-label="Training and certification KPIs">
            {heroKpis.map((card) => {
              const Icon = KPI_ICONS[card.iconClass] ?? GraduationCap;
              return (
                <article key={card.label} className="cttr-kpi-card">
                  <div className="cttr-kpi-head">
                    <small>{card.label}</small>
                    <span className={`cttr-mini-icon ${card.iconClass}`.trim()}>
                      <Icon size={20} />
                    </span>
                  </div>
                  <strong>{card.value}</strong>
                  <span>{card.sub}</span>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <div className="cttr-section-title" id="cttr-cert">
        <div>
          <h2>Certification and Training Status</h2>
          <p>
            Two large panels replace small isolated donuts, making the qualification gap
            and training completion backlog easier to read.
          </p>
        </div>
        <button
          type="button"
          className="cttr-section-action"
          onClick={() => scrollToId("cttr-demand")}
        >
          View demand analysis →
        </button>
      </div>

      <section className="cttr-analytics-grid">
        <article className="cttr-panel">
          <div className="cttr-panel-head">
            <div>
              <h3>CTET Qualification Status</h3>
              <p>
                Qualified, unknown, and not-qualified records separated for immediate
                data and training action.
              </p>
            </div>
            <span className="cttr-pill red">Critical gap</span>
          </div>
          <div className="cttr-donut-area">
            <div className="cttr-big-donut" style={ctetDonut.style} aria-hidden="true">
              <div className="cttr-donut-center">
                <strong>{ctetDonut.centerPct}</strong>
                <span>Qualified</span>
              </div>
            </div>
            <div className="cttr-legend-list">
              {ctetDonut.legend.map((row) => (
                <div key={row.label} className="cttr-legend-item">
                  <span className="cttr-legend-left">
                    <i className={`cttr-dot ${row.dotClass}`.trim()} />
                    {row.label}
                  </span>
                  <span className="cttr-legend-value">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="cttr-panel">
          <div className="cttr-panel-head">
            <div>
              <h3>NISHTHA Training Status</h3>
              <p>
                Completion coverage is visible with pending volume, so block teams can
                plan training batches.
              </p>
            </div>
            <span className="cttr-pill">Completion</span>
          </div>
          <div className="cttr-donut-area">
            <div className="cttr-big-donut" style={nishthaDonut.style} aria-hidden="true">
              <div className="cttr-donut-center">
                <strong>{nishthaDonut.centerPct}</strong>
                <span>Completed</span>
              </div>
            </div>
            <div className="cttr-legend-list">
              {nishthaDonut.legend.map((row) => (
                <div key={row.label} className="cttr-legend-item">
                  <span className="cttr-legend-left">
                    <i className={`cttr-dot ${row.dotClass}`.trim()} />
                    {row.label}
                  </span>
                  <span className="cttr-legend-value">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </article>
      </section>

      <div className="cttr-section-title" id="cttr-demand">
        <div>
          <h2>Training Demand Analysis</h2>
          <p>
            Demand is shown as a ranked bar chart plus program cards so officers can
            prioritize batch planning instead of reading raw chart values.
          </p>
        </div>
        <button
          type="button"
          className="cttr-section-action"
          onClick={() => scrollToId("cttr-actions")}
        >
          Go to action queue →
        </button>
      </div>

      <section className="cttr-panel cttr-demand-panel">
        <div className="cttr-demand-head">
          <div>
            <h3>Ranked Training Demand</h3>
            <p>Estimated demand by training type across the selected district scope.</p>
          </div>
          <span className="cttr-pill">Teacher capacity</span>
        </div>
        <div className="cttr-bar-list">
          {demandBars.map((row) => (
            <div key={row.displayLabel} className="cttr-hbar-row">
              <div className="cttr-hbar-label">{row.label}</div>
              <div className="cttr-hbar-track">
                <div className={`cttr-hbar ${row.barClass}`.trim()} style={{ width: `${row.width}%` }} />
              </div>
              <div className="cttr-hbar-value">{row.display}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="cttr-program-grid" id="cttr-programs">
        {programCards.map((card) => {
          const Icon = PROGRAM_ICONS[card.iconClass] ?? Users;
          return (
            <article key={card.title} className="cttr-program-card">
              <div className="cttr-program-top">
                <div className={`cttr-icon-box ${card.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`cttr-tag ${card.tagClass}`.trim()}>{card.tag}</span>
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <strong>{card.display}</strong>
              <p>{card.description}</p>
              <div className="cttr-progress-line">
                <i style={{ width: `${card.barWidth}%`, background: card.barColor }} />
              </div>
            </article>
          );
        })}
      </section>

      <div className="cttr-section-title" id="cttr-actions">
        <div>
          <h2>Priority Queue and Monitoring Plan</h2>
          <p>
            Operational views are moved below the analytics so users understand the gap
            before deciding who should act.
          </p>
        </div>
        <button
          type="button"
          className="cttr-section-action"
          onClick={() => scrollToId("cttr-hero")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="cttr-two-column">
        <article className="cttr-panel cttr-table-card">
          <div className="cttr-table-head">
            <div>
              <h3>Program Monitoring Watchlist</h3>
              <p>Training categories ranked by current action priority.</p>
            </div>
            <span className="cttr-pill">Top 4</span>
          </div>
          <table aria-label="Training watchlist">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Program</th>
                <th>Gap / Demand</th>
                <th>Priority</th>
                <th>Suggested Owner</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.map((row) => (
                <tr key={row.rank}>
                  <td>{row.rank}</td>
                  <td>{row.program}</td>
                  <td className="score">{row.gap}</td>
                  <td>{row.priority}</td>
                  <td>{row.owner}</td>
                  <td>
                    <span className={`cttr-tag ${row.statusClass}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="cttr-panel">
          <div className="cttr-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Three actions for the next district training review.</p>
            </div>
            <span className="cttr-pill red">Priority</span>
          </div>
          <div className="cttr-action-card">
            {actions.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? AlertTriangle;
              return (
                <div key={item.title} className="cttr-action-item">
                  <div className={`cttr-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="cttr-action-footer">
              <b>30-day review target</b>
              <span>
                Reduce unknown CTET records by 100%, improve NISHTHA completion by 10
                percentage points, and publish a block-wise CTET improvement tracker.
              </span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default CTTeacherTrainingTab;
